import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Divider, Collapse } from "@mui/material";
import ReadMoreButton from "../../shared/components/ReadMoreButton";
import { apiRequest } from "../../shared/api/apiClient";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import IconButton from "@mui/material/IconButton";
import LoadingSpinner from "../../shared/components/LoadingSpinner";
import { useParams } from "react-router-dom";
import KaptiveSummaryTable from "../../shared/components/KaptiveSummaryTable";

export default function AnalyzeResultsKaptive() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;

    const [kaptiveData, setKaptiveData] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id || !token) return;

            setLoading(true);

            try {
                const params = new URLSearchParams({
                    study_id: id,
                    token,
                });

                const endpoint = `kaptive.php?${params.toString()}`;

                const data = await apiRequest(endpoint, {
                    method: "GET",
                });

                if (!cancelled) {
                    setKaptiveData(data ?? {});
                }

            } catch (e) {
                if (!cancelled) {
                    console.error(e);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };

    }, [id, token]);
    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
            }}
        >
            {/* Title block */}
            <Box sx={{ width: "100%", mb: 6 }}>
                <Typography variant="h4">Kaptive</Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 28,
                            borderTopRightRadius: 999,
                            borderBottomRightRadius: 999,
                            background: "#20BC71",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="h5">kaptive</Typography>
                </Box>
            </Box>

            {/* Intro + command */}
            <Grid
                container
                spacing={4}
                sx={{
                    width: "100%",
                    mb: 10,
                    display: "flex",
                    justifyContent: "flex-start",
                }}
            >
                {/* Left column */}
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1">
                                These results were generated by Kaptive.
                            </Typography>
                            <Typography variant="body1">
                                To read the documentation please visit the oficial website
                            </Typography>
                        </Box>

                        <ReadMoreButton href={"https://github.com/klebgenomics/Kaptive"} />
                    </Box>
                </Grid>

                {/* Right column */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                >
                    <Box
                        sx={{
                            background: "#000",
                            color: "#fff",
                            width: "100%",
                            maxWidth: 460,
                            minWidth: { xs: "auto", md: 400 },
                            minHeight: 144,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "left",
                                wordBreak: "break-word",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            }}
                        >
                            <Typography variant="body2">
                                {"kaptive --input ${spades_scaffold} \\"}
                            </Typography>
                            <Typography variant="body2">
                                {"--outdir kaptive/${sample}/"}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Loading / Samples */}
            {loading ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        placeItems: "center",
                        mt: 6,
                        gap: 2,
                    }}
                >
                    <LoadingSpinner size={170} barWidth={18} barHeight={48} />
                    <Typography sx={{ fontSize: 40 }}>Processing files</Typography>
                </Box>
            ) : (
                Object.entries(kaptiveData ?? {}).map(([sampleId, sampleObj]) => (
                    <KaptiveSample sampleId={sampleId} sampleData={sampleObj} key={sampleId} />
                ))
            )}
        </Box>
    );
}

const KaptiveSample = ({ sampleId, sampleData }) => {
    const [openSample, setOpenSample] = useState(false);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Sample header (clickable like Resfinder) */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    cursor: "pointer",
                }}
                onClick={() => setOpenSample((v) => !v)}
            >
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6">Sample {sampleId}</Typography>
                </Box>

                <Box
                    sx={{
                        width: "10%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "end",
                    }}
                >
                    <IconButton
                        sx={{
                            maxWidth: '50px',
                            width: "40px",
                            aspectRatio: '1/1',
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                        }}

                        aria-label={openSample ? "Collapse" : "Expand"}
                    >
                        {openSample ? (
                            <ExpandLessRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        ) : (
                            <ExpandMoreRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={openSample} timeout={240} unmountOnExit>
                <Divider sx={{ borderColor: "#20BC71" }} />

                <Box sx={{ pb: 3, pt: 2 }}>
                    <KaptiveSummaryTable data={sampleData} />
                </Box>
            </Collapse>

            <Divider sx={{ borderColor: "#20BC71" }} />
        </Box>
    );
};